import type { Bundle } from "../schema/store.js";

/** What a member can donate in one go, and what the alliance and the donor get for it (82-alliance.md R78, D72). */
export interface DonationLot {
  id: string;
  currency: "RAT" | "POL" | "ALY" | "ENC" | "credits";
  amount: number;
  /** Alliance XP. */
  xp: number;
  /** Alliance coins for the donor (`itm.alliance_coin`). */
  coins: number;
  /** Progress on the focused alliance building. */
  progress: number;
}

const lot = (currency: DonationLot["currency"], amount: number, xp: number, coins: number, progress: number): DonationLot => ({ id: `lot.${currency.toLowerCase()}_${amount}`, currency, amount, xp, coins, progress });

/** Fourteen lots: three sizes for the bulk resources, two for energy cells, three for credits. */
export const ALLIANCE_DONATIONS: readonly DonationLot[] = [
  lot("RAT", 10_000, 10, 5, 10),
  lot("RAT", 50_000, 45, 25, 45),
  lot("RAT", 200_000, 160, 100, 160),
  lot("POL", 10_000, 10, 5, 10),
  lot("POL", 50_000, 45, 25, 45),
  lot("POL", 200_000, 160, 100, 160),
  lot("ALY", 10_000, 25, 12, 25),
  lot("ALY", 50_000, 110, 60, 110),
  lot("ALY", 200_000, 400, 240, 400),
  lot("ENC", 1_000, 30, 15, 30),
  lot("ENC", 5_000, 140, 75, 140),
  lot("credits", 10, 15, 8, 15),
  lot("credits", 50, 80, 40, 80),
  lot("credits", 200, 350, 180, 350),
];
export const ALLIANCE_DONATION_DAILY_CAP = 20;

/** Cumulative XP needed for each alliance level; level 1 is free. */
export const ALLIANCE_LEVELS: readonly { level: number; xp: number }[] = [
  { level: 1, xp: 0 },
  { level: 2, xp: 500 },
  { level: 3, xp: 1_500 },
  { level: 4, xp: 3_500 },
  { level: 5, xp: 7_000 },
  { level: 6, xp: 12_500 },
  { level: 7, xp: 20_000 },
  { level: 8, xp: 30_000 },
  { level: 9, xp: 45_000 },
  { level: 10, xp: 65_000 },
];

export function allianceLevelFor(xp: number): number {
  let level = 1;
  for (const step of ALLIANCE_LEVELS) if (xp >= step.xp) level = step.level;
  return level;
}

/** Alliance buildings cannot outgrow the alliance: max level = ceil(alliance level / 2), five at most. */
export function allianceBuildingMaxLevel(allianceLevel: number): number {
  return Math.max(1, Math.min(5, Math.ceil(Math.max(1, allianceLevel) / 2)));
}

export type AllianceBuildingId = "ab.muster_hall" | "ab.war_academy" | "ab.bulwark" | "ab.logistics" | "ab.trade_hub" | "ab.embassy";
export type AllianceBuildingEffect = "memberCap" | "atkPct" | "defPct" | "marchGatherPct" | "shopTier" | "assistMax";

export interface AllianceBuildingDef {
  id: AllianceBuildingId;
  name: { de: string; en: string };
  effect: { key: AllianceBuildingEffect; perLevel: number };
  /** Donation progress needed to reach level 1..5 (each level starts from zero). */
  progress: readonly [number, number, number, number, number];
}

const ab = (id: AllianceBuildingId, de: string, en: string, key: AllianceBuildingEffect, perLevel: number, base: number): AllianceBuildingDef => ({
  id,
  name: { de, en },
  effect: { key, perLevel },
  progress: [base, Math.round(base * 2), Math.round(base * 3.5), Math.round(base * 5.5), Math.round(base * 8)],
});

export const ALLIANCE_BUILDINGS: readonly AllianceBuildingDef[] = [
  ab("ab.muster_hall", "Musterhalle", "Muster Hall", "memberCap", 2, 400),
  ab("ab.war_academy", "Kriegsakademie", "War Academy", "atkPct", 1, 600),
  ab("ab.bulwark", "Bollwerk", "Bulwark", "defPct", 1, 600),
  ab("ab.logistics", "Logistikzentrum", "Logistics Centre", "marchGatherPct", 2, 500),
  ab("ab.trade_hub", "Handelsknoten", "Trade Hub", "shopTier", 1, 500),
  ab("ab.embassy", "Botschaft", "Embassy", "assistMax", 1, 450),
];

const buildingMap = new Map(ALLIANCE_BUILDINGS.map((b) => [b.id, b]));
export function allianceBuildingById(id: string): AllianceBuildingDef {
  const b = buildingMap.get(id as AllianceBuildingId);
  if (!b) throw new Error(`unknown alliance building ${id}`);
  return b;
}

export type AllianceGiftTier = "bronze" | "silver" | "gold" | "platinum";
/** What every member claims once from a gift of that tier. */
export const ALLIANCE_GIFT_TIERS: Record<AllianceGiftTier, { reward: Bundle }> = {
  bronze: { reward: { resources: { RAT: 2_000, POL: 2_000 }, items: [{ itemId: "itm.alliance_coin", qty: 5 }] } },
  silver: { reward: { resources: { RAT: 6_000, POL: 6_000, ALY: 1_000 }, items: [{ itemId: "itm.alliance_coin", qty: 15 }, { itemId: "itm.speedup_build_15", qty: 1 }] } },
  gold: { reward: { resources: { RAT: 15_000, POL: 15_000, ALY: 3_000 }, items: [{ itemId: "itm.alliance_coin", qty: 40 }, { itemId: "itm.speedup_build_60", qty: 1 }] } },
  platinum: { reward: { resources: { RAT: 40_000, POL: 40_000, ALY: 8_000, ENC: 1_000 }, items: [{ itemId: "itm.alliance_coin", qty: 100 }, { itemId: "itm.speedup_build_180", qty: 1 }] } },
};
export const ALLIANCE_GIFT_TTL_MS = 86_400_000;
